import React, { useState, useMemo } from 'react';
import ProviderLayout from '@/layouts/ProviderLayout';
import { 
  useListProviderEstimates, 
  useUpdateEstimateStatus, 
  useCreateBundle, 
  useListPatients, 
  useAssignEstimateToPatient,
  useDeleteCostEstimate,
  useListMasterCptCodes,
  useCreateCostEstimate,
  useRedeemVoucherCode
} from '@/lib/api-client-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Check, Clock, Search, Mail, Phone, CalendarDays, Package, 
  Edit2, Share2, MessageSquare, FileText, Download, 
  Plus, LayoutGrid, List, Trash2, ShieldCheck, ArrowUpDown, RefreshCw, X,
  History, CheckCircle2, Receipt,
  SlidersHorizontal, Zap, Copy, Users, FolderKanban
} from 'lucide-react';
import { ListPagination } from '@/components/ui/list-pagination';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from 'wouter';
import { navigatePostSaveBundle } from '@/lib/utils';
import { exportEstimateToCSV, exportEstimateToPDF } from '@/lib/estimateExporter';

interface ServiceItem {
  id?: string;
  code: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

export default function SavedEstimates() {
  const [limit, setLimit] = useState(10);
  const { data: estimates, isLoading, refetch } = useListProviderEstimates();
  const { data: patientsDataObj } = useListPatients({ limit: 1000 });
  const patients = patientsDataObj?.items || [];
  const { data: masterCptData } = useListMasterCptCodes({ limit: 5000 });
  const masterCptList: any[] = useMemo(() => masterCptData?.items || (Array.isArray(masterCptData) ? masterCptData : []), [masterCptData]);
  
  const createCostEstimate = useCreateCostEstimate();
  const updateStatus = useUpdateEstimateStatus();
  const assignPatient = useAssignEstimateToPatient();
  const createBundle = useCreateBundle();
  const deleteEstimate = useDeleteCostEstimate();
  const redeemVoucherMutation = useRedeemVoucherCode();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  // Voucher Redemption State
  const [redeemModalOpen, setRedeemModalOpen] = useState(false);
  const [voucherCodeInput, setVoucherCodeInput] = useState('');

  const handleRedeemVoucher = async (codeToRedeem?: string) => {
    const code = codeToRedeem || voucherCodeInput;
    if (!code) {
      toast({ title: "Redemption Code Required", description: "Please enter a valid voucher code.", variant: "destructive" });
      return;
    }
    try {
      const res = await redeemVoucherMutation.mutateAsync({ redemptionCode: code });
      toast({
        title: "Voucher Successfully Redeemed!",
        description: res.message || `Redemption Voucher Code ${code} verified & marked as REDEEMED for patient service.`
      });
      setRedeemModalOpen(false);
      setVoucherCodeInput('');
      refetch();
    } catch (err: any) {
      toast({
        title: "Voucher Redemption Failed",
        description: err.message || "Invalid or already redeemed voucher code",
        variant: "destructive"
      });
    }
  };

  // State management
  const [viewMode, setViewMode] = useState<'list' | 'grid' | 'grouped'>('list');
  const [patientFilter, setPatientFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'negotiation' | 'approved' | 'paid' | 'templates'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'price_high' | 'price_low' | 'patient_name'>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Duplicate & Share Modal State
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateEstimate, setDuplicateEstimate] = useState<any>(null);
  const [duplicateForm, setDuplicateForm] = useState({
    patientId: '',
    title: '',
    note: '',
    status: 'PENDING'
  });
  
  // Selection & Bulk Actions
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteConfirmData, setDeleteConfirmData] = useState<{ ids: string[]; name: string } | null>(null);

  // Detail Modal
  const [selectedEstimate, setSelectedEstimate] = useState<any>(null);
  
  // Assign Modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignEstimate, setAssignEstimate] = useState<any>(null);
  const [selectedPatientId, setSelectedPatientId] = useState('');

  // Counter Offer Modal (with CPT editing & resubmission)
  const [counterModalOpen, setCounterModalOpen] = useState(false);
  const [counterEstimate, setCounterEstimate] = useState<any>(null);
  const [counterForm, setCounterForm] = useState({ finalPrice: '', discountPercent: '0', note: '' });
  const [counterItems, setCounterItems] = useState<ServiceItem[]>([]);
  const [newCptCode, setNewCptCode] = useState('');
  const [newCptDesc, setNewCptDesc] = useState('');
  const [newCptPrice, setNewCptPrice] = useState('');

  // Mark as Paid / Invoice Modal
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payEstimate, setPayEstimate] = useState<any>(null);
  const [payForm, setPayForm] = useState({
    paymentMethod: 'Credit/Debit Card',
    paymentDate: new Date().toISOString().split('T')[0],
    serviceRenderedDate: new Date().toISOString().split('T')[0],
    paymentReference: '',
    paymentNote: '',
    amountPaid: ''
  });

  // Mark as Completed & Rendered Modal
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [completeEstimate, setCompleteEstimate] = useState<any>(null);
  const [completeForm, setCompleteForm] = useState({
    serviceRenderedDate: new Date().toISOString().split('T')[0],
    note: ''
  });

  // Direct Audit Trail Comment State
  const [newAuditComment, setNewAuditComment] = useState('');

  // Approve & Reject Modals
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [approveEstimate, setApproveEstimate] = useState<any>(null);
  const [approveNote, setApproveNote] = useState('');

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectEstimate, setRejectEstimate] = useState<any>(null);
  const [rejectNote, setRejectNote] = useState('');

  // Convert to Bundle Modal
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [convertEstimate, setConvertEstimate] = useState<any>(null);
  const [bundleForm, setBundleForm] = useState({
    name: '',
    description: '',
    category: 'General',
    discountPercent: '0',
    finalPrice: ''
  });

  // Calculate Summary Statistics
  const stats = useMemo(() => {
    if (!estimates) return { totalCount: 0, totalValue: 0, pendingCount: 0, negotiationCount: 0, approvedCount: 0, paidCount: 0, paidValue: 0 };
    let totalValue = 0;
    let pendingCount = 0;
    let negotiationCount = 0;
    let approvedCount = 0;
    let paidCount = 0;
    let paidValue = 0;

    estimates.forEach((est: any) => {
      const finalAmt = Number(est.finalAmount || est.totalCharges || 0);
      totalValue += finalAmt;

      const s = String(est.status || '').toUpperCase();
      if (s === 'PAID' || s === 'COMPLETED') {
        paidCount++;
        paidValue += finalAmt;
      } else if (s === 'SUBMITTED' || s === 'PENDING') {
        pendingCount++;
      } else if (s === 'PATIENT_PROPOSED' || s === 'PROVIDER_COUNTERED') {
        negotiationCount++;
      } else if (s === 'APPROVED' || s === 'ACCEPTED') {
        approvedCount++;
      }
    });

    return {
      totalCount: estimates.length,
      totalValue,
      pendingCount,
      negotiationCount,
      approvedCount,
      paidCount,
      paidValue
    };
  }, [estimates]);

  // Mark as Paid Handler
  const handleOpenPayModal = (est: any) => {
    setPayEstimate(est);
    const amount = Number(est.finalAmount || est.totalCharges || 0).toFixed(2);
    const defaultRenderedDate = est.serviceRenderedDate 
      ? new Date(est.serviceRenderedDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0];

    setPayForm({
      paymentMethod: 'Credit/Debit Card',
      paymentDate: new Date().toISOString().split('T')[0],
      serviceRenderedDate: defaultRenderedDate,
      paymentReference: '',
      paymentNote: '',
      amountPaid: amount
    });
    setPayModalOpen(true);
  };

  const handleMarkAsPaidSubmit = async () => {
    if (!payEstimate) return;
    try {
      const paidAmt = parseFloat(payForm.amountPaid) || Number(payEstimate.finalAmount || 0);
      await updateStatus.mutateAsync({
        id: payEstimate.id,
        status: 'PAID',
        finalAmount: paidAmt,
        paymentMethod: payForm.paymentMethod,
        paymentDate: payForm.paymentDate,
        serviceRenderedDate: payForm.serviceRenderedDate,
        paymentNote: payForm.paymentNote,
        paymentReference: payForm.paymentReference,
        counterNote: `Invoice Paid ($${paidAmt.toFixed(2)}) via ${payForm.paymentMethod} [Service Rendered: ${payForm.serviceRenderedDate}]${payForm.paymentReference ? ` [Ref #${payForm.paymentReference}]` : ''}`
      });

      toast({
        title: "Estimate Invoice Marked as Paid!",
        description: `Payment of $${paidAmt.toFixed(2)} (${payForm.paymentMethod}) recorded successfully.`
      });

      setPayModalOpen(false);
      setPayEstimate(null);
      refetch();
    } catch (err: any) {
      toast({ title: "Payment Record Error", description: err.message || "Failed to mark invoice as paid", variant: "destructive" });
    }
  };

  // Mark as Completed & Rendered Handler
  const handleOpenCompleteModal = (est: any) => {
    setCompleteEstimate(est);
    const defaultDate = est.serviceRenderedDate 
      ? new Date(est.serviceRenderedDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0];
    setCompleteForm({
      serviceRenderedDate: defaultDate,
      note: 'Service rendered and procedure completed.'
    });
    setCompleteModalOpen(true);
  };

  const handleMarkAsCompletedSubmit = async () => {
    if (!completeEstimate) return;
    try {
      await updateStatus.mutateAsync({
        id: completeEstimate.id,
        status: 'COMPLETED',
        serviceRenderedDate: completeForm.serviceRenderedDate,
        counterNote: `[Service Rendered Date: ${completeForm.serviceRenderedDate}] ${completeForm.note}`
      });

      toast({
        title: "Service Marked as Completed!",
        description: `Service rendered date (${completeForm.serviceRenderedDate}) recorded.`
      });

      setCompleteModalOpen(false);
      setCompleteEstimate(null);
      refetch();
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to mark as completed", variant: "destructive" });
    }
  };

  // Direct Audit Trail Comment Submission
  const handleAddAuditCommentSubmit = async () => {
    if (!selectedEstimate || !newAuditComment.trim()) return;
    try {
      const updatedRes = await updateStatus.mutateAsync({
        id: selectedEstimate.id,
        status: selectedEstimate.status || 'SAVED',
        comment: newAuditComment.trim()
      });

      toast({
        title: "Audit Comment Logged!",
        description: "Your comment was recorded in the estimate audit trail."
      });

      setNewAuditComment('');
      if (updatedRes?.data) {
        setSelectedEstimate(updatedRes.data);
      }
      refetch();
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to post audit comment", variant: "destructive" });
    }
  };

  // Handle Opening Counter Offer Modal (with CPT code list editing)
  const handleOpenCounterModal = (est: any) => {
    setCounterEstimate(est);
    const rawItems = Array.isArray(est.items) ? est.items : [];
    const itemsList: ServiceItem[] = rawItems.length > 0 
      ? rawItems.map((item: any) => ({
          id: item.id || `cpt-${Math.random().toString(36).substr(2,6)}`,
          code: item.code || item.cptCode || 'CUSTOM',
          description: item.description || item.name || 'Medical Service',
          quantity: Number(item.quantity || 1),
          unitPrice: Number(item.unitPrice || item.price || 0)
        }))
      : [{ id: 'cpt-1', code: est.cptCodes || 'CUSTOM', description: est.procedure || est.name || 'Service', quantity: 1, unitPrice: Number(est.totalCharges || est.finalAmount || 0) }];

    setCounterItems(itemsList);

    const calculatedTotal = itemsList.reduce((acc, item) => acc + (item.unitPrice * item.quantity), 0);
    const currentFinal = Number(est.finalAmount || calculatedTotal);
    let initDiscount = Number(est.discount || 0);
    if (initDiscount === 0 && calculatedTotal > 0 && currentFinal < calculatedTotal) {
      initDiscount = Math.round(((calculatedTotal - currentFinal) / calculatedTotal) * 1000) / 10;
    }

    setCounterForm({
      finalPrice: currentFinal.toFixed(2),
      discountPercent: initDiscount.toString(),
      note: ''
    });
    setNewCptCode('');
    setNewCptDesc('');
    setNewCptPrice('');
    setCounterModalOpen(true);
  };

  const handleAddCounterCpt = () => {
    if (!newCptCode) return;
    const price = parseFloat(newCptPrice) || 0;
    const newItem: ServiceItem = {
      id: `cpt-${Date.now()}`,
      code: newCptCode.toUpperCase(),
      description: newCptDesc || 'Added CPT Code',
      quantity: 1,
      unitPrice: price
    };
    const updated = [...counterItems, newItem];
    setCounterItems(updated);

    const newTotal = updated.reduce((acc, item) => acc + (item.unitPrice * item.quantity), 0);
    const disc = parseFloat(counterForm.discountPercent) || 0;
    const calcFinal = Math.max(0, newTotal - (newTotal * disc) / 100).toFixed(2);
    setCounterForm(prev => ({ ...prev, finalPrice: calcFinal }));

    setNewCptCode('');
    setNewCptDesc('');
    setNewCptPrice('');
  };

  const handleRemoveCounterCpt = (index: number) => {
    const updated = counterItems.filter((_, i) => i !== index);
    setCounterItems(updated);
    const newTotal = updated.reduce((acc, item) => acc + (item.unitPrice * item.quantity), 0);
    const disc = parseFloat(counterForm.discountPercent) || 0;
    const calcFinal = Math.max(0, newTotal - (newTotal * disc) / 100).toFixed(2);
    setCounterForm(prev => ({ ...prev, finalPrice: calcFinal }));
  };

  const handleSendCounterOfferSubmit = async () => {
    if (!counterEstimate || !counterForm.finalPrice) return;
    try {
      const finalAmt = parseFloat(counterForm.finalPrice);
      const discountVal = parseFloat(counterForm.discountPercent) || 0;
      const totalCharges = counterItems.reduce((acc, i) => acc + (i.unitPrice * i.quantity), 0);

      await updateStatus.mutateAsync({
        id: counterEstimate.id,
        status: 'PROVIDER_COUNTERED',
        finalAmount: finalAmt,
        discount: discountVal,
        totalCharges: totalCharges > 0 ? totalCharges : finalAmt,
        items: counterItems,
        counterNote: counterForm.note
      });
      toast({ title: "Counter Offer Resubmitted!", description: "Revised CPT codes and pricing sent to patient for review." });
      setCounterModalOpen(false);
      setCounterEstimate(null);
      refetch();
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to send counter offer", variant: "destructive" });
    }
  };

  const handleOpenApproveModal = (est: any) => {
    setApproveEstimate(est);
    setApproveNote('');
    setApproveModalOpen(true);
  };

  const handleSendApproveSubmit = async () => {
    if (!approveEstimate) return;
    try {
      await updateStatus.mutateAsync({
        id: approveEstimate.id,
        status: 'APPROVED',
        counterNote: approveNote || 'Approved service estimate'
      });
      toast({ title: "Estimate Approved!", description: "Member has been notified of estimate approval." });
      setApproveModalOpen(false);
      setApproveEstimate(null);
      refetch();
    } catch (err: any) {
      toast({ title: "Approval Error", description: err.message || "Failed to approve estimate", variant: "destructive" });
    }
  };

  const handleOpenRejectModal = (est: any) => {
    setRejectEstimate(est);
    setRejectNote('');
    setRejectModalOpen(true);
  };

  const handleSendRejectSubmit = async () => {
    if (!rejectEstimate) return;
    try {
      await updateStatus.mutateAsync({
        id: rejectEstimate.id,
        status: 'REJECTED',
        counterNote: rejectNote || 'Estimate unable to be fulfilled'
      });
      toast({ title: "Estimate Declined", description: "Member notified with feedback." });
      setRejectModalOpen(false);
      setRejectEstimate(null);
      refetch();
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to decline estimate", variant: "destructive" });
    }
  };

  const handleOpenAssignModal = (est: any) => {
    setAssignEstimate(est);
    setSelectedPatientId('');
    setAssignModalOpen(true);
  };

  const handleAssignPatientSubmit = async () => {
    if (!assignEstimate || !selectedPatientId) {
      toast({ title: "Patient Required", description: "Please select a patient to send this estimate to.", variant: "destructive" });
      return;
    }
    try {
      await assignPatient.mutateAsync({ id: assignEstimate.id, memberId: selectedPatientId });
      toast({ title: "Estimate Sent", description: "Estimate sent to patient and tracked as PENDING review." });
      setAssignModalOpen(false);
      setAssignEstimate(null);
      refetch();
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to send estimate", variant: "destructive" });
    }
  };

  // Duplicate & Share to New Patient
  const handleOpenDuplicateModal = (est: any, targetPatientId?: string) => {
    const estTitle = est?.name || est?.title || est?.procedure || 'Cost Estimate';
    setDuplicateEstimate(est);
    setDuplicateForm({
      patientId: targetPatientId || est?.memberId || est?.member?.id || '',
      title: `${estTitle} (Copy)`,
      note: 'Good Faith Estimate cloned for new patient consultation.',
      status: targetPatientId || est?.memberId ? 'PENDING' : 'SAVED'
    });
    setDuplicateModalOpen(true);
  };

  const handleDuplicateSubmit = async () => {
    if (!duplicateEstimate) return;
    try {
      const rawItems = Array.isArray(duplicateEstimate.items) ? duplicateEstimate.items : [];
      const items = rawItems.length > 0 
        ? rawItems.map((item: any) => ({
            code: item.code || item.cptCode || 'CUSTOM',
            description: item.description || item.name || 'Medical Service',
            quantity: Number(item.quantity || 1),
            unitPrice: Number(item.unitPrice || 0)
          }))
        : [{
            code: duplicateEstimate.cptCodes || 'ESTIMATE',
            description: duplicateEstimate.procedure || duplicateForm.title,
            quantity: 1,
            unitPrice: Number(duplicateEstimate.finalAmount || duplicateEstimate.totalCharges || 0)
          }];

      await createCostEstimate.mutateAsync({
        name: duplicateForm.title,
        memberId: duplicateForm.patientId || null,
        totalCharges: Number(duplicateEstimate.totalCharges || duplicateEstimate.finalAmount || 0),
        discount: Number(duplicateEstimate.discount || 0),
        finalAmount: Number(duplicateEstimate.finalAmount || 0),
        items,
        status: duplicateForm.patientId ? (duplicateForm.status || 'PENDING') : 'SAVED',
        patientNote: duplicateForm.note
      });

      const matchedPatient = patients.find((p: any) => p.id === duplicateForm.patientId || p.userId === duplicateForm.patientId);
      const patientLabel = matchedPatient 
        ? (matchedPatient.displayName || `${matchedPatient.firstName || ''} ${matchedPatient.lastName || ''}`.trim())
        : (duplicateForm.patientId ? 'Patient' : 'Master Template');

      toast({
        title: "Estimate Duplicated!",
        description: duplicateForm.patientId 
          ? `Estimate duplicated & shared directly with ${patientLabel}.` 
          : `Estimate duplicated as a master practice template.`
      });

      setDuplicateModalOpen(false);
      setDuplicateEstimate(null);
      refetch();
    } catch (err: any) {
      toast({ title: "Duplication Error", description: err.message || "Failed to duplicate estimate", variant: "destructive" });
    }
  };

  const handleAcceptPatientProposal = async (est: any) => {
    try {
      await updateStatus.mutateAsync({
        id: est.id,
        status: 'ACCEPTED',
        finalAmount: est.finalAmount,
        counterNote: 'Provider accepted patient price proposal'
      });
      toast({ title: "Proposal Accepted", description: `Estimate agreed & accepted at $${Number(est.finalAmount).toFixed(2)}.` });
      refetch();
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to accept proposal", variant: "destructive" });
    }
  };

  const handleEditInCostEstimator = (est: any) => {
    const totalCharges = Number(est.totalCharges || est.estimatedCost || 0);
    const finalAmount = Number(est.finalAmount || est.patientResponsibility || totalCharges);
    let discountPct = Number(est.discount || 0);
    if (discountPct === 0 && totalCharges > 0 && finalAmount < totalCharges) {
      discountPct = Math.round(((totalCharges - finalAmount) / totalCharges) * 1000) / 10;
    }

    const editPayload = {
      id: est.id,
      name: est.name || est.title || est.procedure || "Custom Cost Estimate",
      memberId: est.memberId || est.member?.userId || est.member?.id || "",
      patientName: est.member?.displayName || (est.member?.firstName ? `${est.member.firstName} ${est.member.lastName}`.trim() : ""),
      patientEmail: est.member?.email || "",
      discount: discountPct,
      totalCharges: totalCharges,
      finalAmount: finalAmount,
      items: (Array.isArray(est.items) && est.items.length > 0)
        ? est.items.map((item: any, idx: number) => ({
            id: item.id || `est-item-${idx}-${Date.now()}`,
            code: item.code || item.cptCode || "CUSTOM",
            description: item.description || item.name || item.code || "Service Item",
            quantity: Number(item.quantity || 1),
            unitPrice: Number(item.unitPrice || item.price || 0),
            standardPrice: Number(item.standardPrice || item.unitPrice || 0),
            currentPct: item.currentPct !== undefined ? Number(item.currentPct) : 100
          }))
        : (est.cptCodes || "CUSTOM").split(',').map((cpt: string, idx: number) => ({
            id: `est-item-${idx}-${Date.now()}`,
            code: cpt.trim(),
            description: est.procedure || 'Medical Service',
            quantity: 1,
            unitPrice: finalAmount,
            standardPrice: totalCharges || finalAmount,
            currentPct: 100
          }))
    };
    sessionStorage.setItem("edit_estimate", JSON.stringify(editPayload));
    setLocation("/provider/cost-estimator");
  };

  const handleOpenConvertModal = (est: any) => {
    const patientName = est.member?.displayName || est.member?.firstName || "Patient";
    const defaultName = est.name || est.title || `${patientName} Care Package`;
    const total = Number(est.totalCharges || 0);
    const discount = Number(est.discount || 0);
    const finalAmt = Number(est.finalAmount || total);

    setBundleForm({
      name: defaultName,
      description: `Created from estimate for ${patientName}`,
      category: "General",
      discountPercent: discount.toString(),
      finalPrice: finalAmt.toFixed(2)
    });
    setConvertEstimate(est);
    setConvertModalOpen(true);
  };

  const handleSaveConvertedBundle = async () => {
    if (!convertEstimate || !bundleForm.name) return;
    try {
      const cptItems = (convertEstimate.items || []).filter((i: any) => i.code !== 'CUSTOM');
      const cptCodeIds = cptItems.map((i: any) => i.cptCodeId || i.code || i.id).filter((c: any) => !!c);
      const cptUnits: Record<string, number> = {};
      cptItems.forEach((i: any) => {
        const key = i.cptCodeId || i.code || i.id;
        if (key) cptUnits[key] = Number(i.quantity) || 1;
      });
      const customItems = (convertEstimate.items || [])
        .filter((i: any) => i.code === 'CUSTOM')
        .map((i: any) => ({ name: i.description || i.code, price: Number(i.unitPrice || 0) }));

      await createBundle.mutateAsync({
        data: {
          name: bundleForm.name,
          description: bundleForm.description,
          category: bundleForm.category,
          totalPrice: Number(convertEstimate.totalCharges || 0),
          discountPercent: parseFloat(bundleForm.discountPercent) || 0,
          finalPrice: parseFloat(bundleForm.finalPrice) || Number(convertEstimate.totalCharges || 0),
          cptCodeIds,
          cptUnits,
          customItems,
          type: 'INDIVIDUAL'
        }
      });

      toast({ title: "Bundle Created", description: `"${bundleForm.name}" added to your practice bundles!` });
      setConvertModalOpen(false);
      setConvertEstimate(null);
      navigatePostSaveBundle(setLocation);
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to create bundle", variant: "destructive" });
    }
  };

  // Single & Bulk Delete logic
  const handleDeleteConfirm = async () => {
    if (!deleteConfirmData) return;
    try {
      for (const id of deleteConfirmData.ids) {
        await deleteEstimate.mutateAsync({ id });
      }
      toast({ 
        title: "Deleted", 
        description: deleteConfirmData.ids.length > 1 
          ? `${deleteConfirmData.ids.length} estimates deleted successfully.` 
          : `Estimate "${deleteConfirmData.name}" deleted successfully.` 
      });
      setSelectedIds(prev => prev.filter(id => !deleteConfirmData.ids.includes(id)));
      setDeleteConfirmData(null);
      refetch();
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to delete estimate", variant: "destructive" });
    }
  };

  // Bulk Export CSV
  const handleBulkExportCSV = () => {
    const targets = (estimates || []).filter((e: any) => selectedIds.includes(e.id));
    if (targets.length === 0) return;

    targets.forEach((target: any) => {
      exportEstimateToCSV({
        title: target.name || target.title || 'cost-estimate',
        patientName: target.member?.displayName || (target.member?.firstName ? `${target.member.firstName} ${target.member.lastName}` : 'Patient'),
        patientEmail: target.member?.email,
        date: target.createdAt,
        items: (target.items || []).map((i: any) => ({
          code: i.code || 'CUSTOM',
          description: i.description || i.name || 'Service Item',
          quantity: i.quantity || 1,
          unitPrice: Number(i.unitPrice || 0)
        })),
        totalCharges: Number(target.totalCharges || 0),
        discount: Number(target.discount || 0),
        finalAmount: Number(target.finalAmount || 0)
      });
    });

    toast({ title: "Export Complete", description: `Exported ${targets.length} estimate records to CSV.` });
  };

  // Filter & Search Logic
  const filteredEstimates = useMemo(() => {
    if (!estimates) return [];
    let list = estimates.filter((est: any) => {
      const estName = est.name || est.title || est.procedure || "";
      const patientName = est.member?.displayName || (est.member?.firstName ? `${est.member.firstName} ${est.member.lastName}` : "");
      const email = est.member?.email || "";
      const phone = est.member?.phone || "";
      const codes = (est.items || []).map((i: any) => i.code || "").join(" ");
      const statusStr = String(est.status || '').toUpperCase();

      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = 
        estName.toLowerCase().includes(searchLower) || 
        patientName.toLowerCase().includes(searchLower) || 
        email.toLowerCase().includes(searchLower) ||
        phone.toLowerCase().includes(searchLower) ||
        codes.toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;

      if (patientFilter !== 'all') {
        if (patientFilter === 'unassigned') {
          if (est.memberId || est.member) return false;
        } else {
          const matchPid = est.memberId === patientFilter || est.member?.id === patientFilter || est.member?.userId === patientFilter;
          if (!matchPid) return false;
        }
      }

      if (activeTab === 'pending') {
        return statusStr === 'SUBMITTED' || statusStr === 'PENDING';
      }
      if (activeTab === 'negotiation') {
        return statusStr === 'PATIENT_PROPOSED' || statusStr === 'PROVIDER_COUNTERED';
      }
      if (activeTab === 'approved') {
        return statusStr === 'APPROVED' || statusStr === 'ACCEPTED';
      }
      if (activeTab === 'paid') {
        return statusStr === 'PAID' || statusStr === 'COMPLETED';
      }
      if (activeTab === 'templates') {
        return (!est.status || statusStr === 'SAVED') && !est.memberId;
      }
      return true;
    });

    list = [...list].sort((a: any, b: any) => {
      if (sortBy === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sortBy === 'oldest') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      if (sortBy === 'price_high') return Number(b.finalAmount || 0) - Number(a.finalAmount || 0);
      if (sortBy === 'price_low') return Number(a.finalAmount || 0) - Number(b.finalAmount || 0);
      if (sortBy === 'patient_name') {
        const nameA = a.member?.displayName || a.member?.firstName || a.name || "";
        const nameB = b.member?.displayName || b.member?.firstName || b.name || "";
        return nameA.localeCompare(nameB);
      }
      return 0;
    });

    return list;
  }, [estimates, searchQuery, activeTab, patientFilter, sortBy]);

  // Group Estimates by Patient
  const groupedByPatient = useMemo(() => {
    const map = new Map<string, { patientId: string; patientName: string; email?: string; phone?: string; estimates: any[]; totalValue: number }>();

    filteredEstimates.forEach((est: any) => {
      const pid = est.memberId || est.member?.id || 'unassigned';
      const patientName = est.member?.displayName || (est.member?.firstName ? `${est.member.firstName} ${est.member.lastName}`.trim() : null) || 'Unassigned Master Templates';
      const email = est.member?.email;
      const phone = est.member?.phone;
      const amount = Number(est.finalAmount || est.totalCharges || 0);

      if (!map.has(pid)) {
        map.set(pid, {
          patientId: pid,
          patientName,
          email,
          phone,
          estimates: [],
          totalValue: 0
        });
      }

      const group = map.get(pid)!;
      group.estimates.push(est);
      group.totalValue += amount;
    });

    return Array.from(map.values());
  }, [filteredEstimates]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredEstimates.length / limit) || 1;
  const paginatedEstimates = useMemo(() => {
    return filteredEstimates.slice((currentPage - 1) * limit, currentPage * limit);
  }, [filteredEstimates, currentPage, limit]);

  const isAllSelected = paginatedEstimates.length > 0 && paginatedEstimates.every((e: any) => selectedIds.includes(e.id));
  const toggleSelectAll = () => {
    if (isAllSelected) {
      const pageIds = paginatedEstimates.map((e: any) => e.id);
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      const pageIds = paginatedEstimates.map((e: any) => e.id);
      setSelectedIds(prev => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };

  const getStatusBadge = (est: any) => {
    const s = String(typeof est === 'string' ? est : (est?.status || 'SAVED')).toUpperCase();
    const lastActionBy = typeof est === 'object' && est !== null ? (est.lastActionBy || est.items?.lastActionBy || 'MEMBER') : 'MEMBER';

    if (s === 'PENDING' || s === 'SUBMITTED') {
      if (lastActionBy === 'PROVIDER') {
        return <Badge className="bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/60 dark:text-amber-300 font-bold px-2.5 py-0.5 rounded-full text-xs">Pending Member Review</Badge>;
      }
      return <Badge className="bg-amber-500 text-white font-bold animate-pulse px-2.5 py-0.5 rounded-full text-xs shadow-2xs">Pending Your Review</Badge>;
    }
    if (s === 'APPROVED' || s === 'ACCEPTED') {
      return <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 font-bold px-2.5 py-0.5 rounded-full text-xs">Accepted</Badge>;
    }
    if (s === 'REJECTED' || s === 'DECLINED') {
      return <Badge className="bg-rose-100 text-rose-900 border-rose-300 dark:bg-rose-950/60 dark:text-rose-300 font-bold px-2.5 py-0.5 rounded-full text-xs">Rejected</Badge>;
    }
    if (s === 'PAID WAITING SERVICE' || s === 'PAID') {
      return <Badge className="bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-950/60 dark:text-blue-300 font-bold px-2.5 py-0.5 rounded-full text-xs">Paid - Awaiting Service</Badge>;
    }
    if (s === 'COMPLETED') {
      return <Badge className="bg-emerald-600 text-white border-emerald-700 font-bold px-2.5 py-0.5 rounded-full text-xs flex items-center gap-1 shadow-2xs"><CheckCircle2 className="w-3.5 h-3.5" /> Completed</Badge>;
    }
    if (s === 'REFUNDED') {
      return <Badge className="bg-orange-100 text-orange-900 border-orange-300 font-bold px-2.5 py-0.5 rounded-full text-xs">Refunded</Badge>;
    }
    if (s === 'CANCELLED') {
      return <Badge className="bg-slate-100 text-slate-700 border-slate-300 font-medium px-2.5 py-0.5 rounded-full text-xs">Cancelled</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 font-medium px-2.5 py-0.5 rounded-full text-xs">Draft Quote</Badge>;
  };

  return (
    <ProviderLayout>
      <div className="w-full pt-4 sm:pt-6 lg:pt-8 flex flex-col min-h-[calc(100vh-6rem)] space-y-4 sm:space-y-6">
        
        {/* Top Header */}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2.5 sm:p-3 bg-primary/10 rounded-xl flex-shrink-0">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                Estimates & Quotes / Invoices
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm mt-0.5 sm:mt-1">
                Manage financial quotes, revise CPT billing codes, negotiate pricing, and record invoice payments.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-2.5 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="rounded-xl h-9 sm:h-10 gap-1.5 border-slate-200 dark:border-slate-800 font-semibold text-xs text-slate-700 dark:text-slate-300"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
            {selectedIds.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkExportCSV}
                className="rounded-xl h-9 sm:h-10 gap-1.5 border-primary/30 text-primary font-bold text-xs bg-primary/5 hover:bg-primary/10"
              >
                <Download className="w-3.5 h-3.5" /> Export Selected ({selectedIds.length})
              </Button>
            )}
            <Button
              onClick={() => setLocation('/provider/cost-estimator')}
              className="rounded-xl h-9 sm:h-10 gap-2 font-bold text-xs bg-primary hover:bg-primary/90 text-white shadow-md shadow-primary/20"
            >
              <Plus className="w-4 h-4" /> Create Estimate Quote
            </Button>
          </div>
        </div>

        {/* Executive Summary Stats Banner */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3 xl:gap-3.5 2xl:gap-4">
          <Card className="rounded-2xl border-slate-200 dark:border-slate-800 shadow-xs bg-white dark:bg-slate-900 transition-all hover:shadow-sm h-full">
            <CardContent className="p-3.5 sm:p-4 lg:p-3.5 xl:p-4 2xl:p-5 flex flex-col justify-between h-full">
              <p className="text-[10.5px] sm:text-[11px] lg:text-[11px] xl:text-xs font-bold text-slate-500 uppercase tracking-wider leading-none">
                Total Quotes
              </p>
              <div className="flex items-center justify-between gap-2 xl:gap-3 my-2 xl:my-2.5">
                <h3 className="text-2xl lg:text-2xl xl:text-2xl 2xl:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                  {stats.totalCount}
                </h3>
                <div className="w-10 h-10 lg:w-10 lg:h-10 xl:w-10.5 xl:h-10.5 2xl:w-11 2xl:h-11 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0 shadow-2xs">
                  <FileText className="w-5 h-5 xl:w-5.5 xl:h-5.5" strokeWidth={2} />
                </div>
              </div>
              <p className="text-[10.5px] sm:text-[11px] lg:text-[11px] xl:text-xs font-bold text-primary leading-tight">
                ${stats.totalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })} Total Value
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-amber-200/80 dark:border-amber-900/50 shadow-xs bg-[#FFFDF7] dark:bg-amber-950/20 transition-all hover:shadow-sm h-full">
            <CardContent className="p-3.5 sm:p-4 lg:p-3.5 xl:p-4 2xl:p-5 flex flex-col justify-between h-full">
              <p className="text-[10.5px] sm:text-[11px] lg:text-[11px] xl:text-xs font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider leading-none">
                Pending Review
              </p>
              <div className="flex items-center justify-between gap-2 xl:gap-3 my-2 xl:my-2.5">
                <h3 className="text-2xl lg:text-2xl xl:text-2xl 2xl:text-3xl font-extrabold text-amber-950 dark:text-amber-200 tracking-tight">
                  {stats.pendingCount}
                </h3>
                <div className="w-10 h-10 lg:w-10 lg:h-10 xl:w-10.5 xl:h-10.5 2xl:w-11 2xl:h-11 rounded-xl bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 flex items-center justify-center shrink-0 shadow-2xs">
                  <Clock className="w-5 h-5 xl:w-5.5 xl:h-5.5" strokeWidth={2} />
                </div>
              </div>
              <p className="text-[10.5px] sm:text-[11px] lg:text-[11px] xl:text-xs font-semibold text-amber-700 dark:text-amber-400 leading-tight">
                Requires response
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-purple-200/80 dark:border-purple-900/50 shadow-xs bg-[#FCFAFF] dark:bg-purple-950/20 transition-all hover:shadow-sm h-full">
            <CardContent className="p-3.5 sm:p-4 lg:p-3.5 xl:p-4 2xl:p-5 flex flex-col justify-between h-full">
              <p className="text-[10.5px] sm:text-[11px] lg:text-[11px] xl:text-xs font-bold text-purple-800 dark:text-purple-400 uppercase tracking-wider leading-none">
                In Negotiation
              </p>
              <div className="flex items-center justify-between gap-2 xl:gap-3 my-2 xl:my-2.5">
                <h3 className="text-2xl lg:text-2xl xl:text-2xl 2xl:text-3xl font-extrabold text-purple-950 dark:text-purple-200 tracking-tight">
                  {stats.negotiationCount}
                </h3>
                <div className="w-10 h-10 lg:w-10 lg:h-10 xl:w-10.5 xl:h-10.5 2xl:w-11 2xl:h-11 rounded-xl bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 flex items-center justify-center shrink-0 shadow-2xs">
                  <MessageSquare className="w-5 h-5 xl:w-5.5 xl:h-5.5" strokeWidth={2} />
                </div>
              </div>
              <p className="text-[10.5px] sm:text-[11px] lg:text-[11px] xl:text-xs font-semibold text-purple-700 dark:text-purple-400 leading-tight">
                Counter offers active
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-blue-200/80 dark:border-blue-900/50 shadow-xs bg-[#F8FAFF] dark:bg-blue-950/20 transition-all hover:shadow-sm h-full">
            <CardContent className="p-3.5 sm:p-4 lg:p-3.5 xl:p-4 2xl:p-5 flex flex-col justify-between h-full">
              <p className="text-[10.5px] sm:text-[11px] lg:text-[11px] xl:text-xs font-bold text-blue-800 dark:text-blue-400 uppercase tracking-wider leading-none">
                Approved Quotes
              </p>
              <div className="flex items-center justify-between gap-2 xl:gap-3 my-2 xl:my-2.5">
                <h3 className="text-2xl lg:text-2xl xl:text-2xl 2xl:text-3xl font-extrabold text-blue-950 dark:text-blue-200 tracking-tight">
                  {stats.approvedCount}
                </h3>
                <div className="w-10 h-10 lg:w-10 lg:h-10 xl:w-10.5 xl:h-10.5 2xl:w-11 2xl:h-11 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0 shadow-2xs">
                  <Check className="w-5 h-5 xl:w-5.5 xl:h-5.5" strokeWidth={2.2} />
                </div>
              </div>
              <p className="text-[10.5px] sm:text-[11px] lg:text-[11px] xl:text-xs font-semibold text-blue-700 dark:text-blue-400 leading-tight">
                Agreed & ready for pay
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-emerald-300/80 dark:border-emerald-800/80 shadow-xs bg-[#F6FDF9] dark:bg-emerald-950/20 transition-all hover:shadow-sm sm:col-span-2 md:col-span-1 lg:col-span-1 h-full">
            <CardContent className="p-3.5 sm:p-4 lg:p-3.5 xl:p-4 2xl:p-5 flex flex-col justify-between h-full">
              <p className="text-[10.5px] sm:text-[11px] lg:text-[11px] xl:text-xs font-bold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider leading-none">
                Paid Invoices
              </p>
              <div className="flex items-center justify-between gap-2 xl:gap-3 my-2 xl:my-2.5">
                <h3 className="text-2xl lg:text-2xl xl:text-2xl 2xl:text-3xl font-extrabold text-emerald-950 dark:text-emerald-100 tracking-tight">
                  {stats.paidCount}
                </h3>
                <div className="w-10 h-10 lg:w-10 lg:h-10 xl:w-10.5 xl:h-10.5 2xl:w-11 2xl:h-11 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0 shadow-2xs">
                  <Receipt className="w-5 h-5 xl:w-5.5 xl:h-5.5" strokeWidth={2} />
                </div>
              </div>
              <p className="text-[10.5px] sm:text-[11px] lg:text-[11px] xl:text-xs font-extrabold text-emerald-700 dark:text-emerald-300 leading-tight">
                ${stats.paidValue.toLocaleString('en-US', { minimumFractionDigits: 2 })} Collected
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Toolbar & Filter Bar */}
        <div className="flex flex-col gap-4 bg-white dark:bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
          
          {/* Status Filter Tabs & View Controls */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
            {/* Status Filter Tabs */}
            <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-none min-w-0 pb-1 md:pb-0">
              {[
                { id: 'all', label: 'All Quotes & Invoices', count: estimates?.length || 0 },
                { id: 'pending', label: 'Pending Review', count: stats.pendingCount },
                { id: 'negotiation', label: 'Negotiations', count: stats.negotiationCount },
                { id: 'approved', label: 'Approved Quotes', count: stats.approvedCount },
                { id: 'paid', label: 'Paid Invoices', count: stats.paidCount },
                { id: 'templates', label: 'Templates / Drafts', count: (estimates || []).filter((e: any) => (!e.status || String(e.status).toUpperCase() === 'SAVED') && !e.memberId).length }
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => { setActiveTab(tab.id as any); setCurrentPage(1); }}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-primary text-white shadow-xs'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  {tab.label}
                  <span className={`px-2 py-0.5 text-[10px] rounded-full font-bold ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-200/80 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            {/* View Mode Switcher */}
            <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl shrink-0 self-end md:self-auto h-9.5">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`p-1.5 sm:px-2.5 sm:py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${
                  viewMode === 'list' 
                    ? 'bg-white dark:bg-slate-900 text-primary shadow-xs font-bold' 
                    : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
                title="Compact List View"
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline text-[11px]">List</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`p-1.5 sm:px-2.5 sm:py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${
                  viewMode === 'grid' 
                    ? 'bg-white dark:bg-slate-900 text-primary shadow-xs font-bold' 
                    : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
                title="Card Grid View"
              >
                <LayoutGrid className="w-4 h-4" />
                <span className="hidden sm:inline text-[11px]">Grid</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('grouped')}
                className={`p-1.5 sm:px-2.5 sm:py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${
                  viewMode === 'grouped' 
                    ? 'bg-white dark:bg-slate-900 text-primary shadow-xs font-bold' 
                    : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
                title="Grouped by Patient View"
              >
                <FolderKanban className="w-4 h-4" />
                <span className="hidden sm:inline text-[11px]">Grouped</span>
              </button>
            </div>
          </div>

          {/* Search, Filter & Action Controls */}
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 pt-0.5">
            {/* Search Input */}
            <div className="relative flex-1 min-w-[220px] max-w-full lg:max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search estimate quote, patient, CPT code..."
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="pl-9.5 pr-8 bg-slate-50 dark:bg-slate-950 rounded-xl text-xs border-slate-200 dark:border-slate-800 h-10 w-full focus:bg-white dark:focus:bg-slate-900 transition-colors shadow-2xs"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')} 
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Filter Dropdowns & Redeem Button */}
            <div className="flex items-center gap-2.5 sm:gap-3 flex-wrap sm:flex-nowrap justify-between lg:justify-end">
              <div className="flex-1 sm:flex-initial min-w-[160px] sm:w-[195px]">
                <Select value={patientFilter} onValueChange={(v: string) => { setPatientFilter(v); setCurrentPage(1); }}>
                  <SelectTrigger className="h-10 text-xs rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 w-full shadow-2xs">
                    <div className="flex items-center gap-2 truncate">
                      <Users className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <SelectValue placeholder="All Patients" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Patients & Templates</SelectItem>
                    <SelectItem value="unassigned">Unassigned Templates</SelectItem>
                    {patients.map((p: any) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.displayName || p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1 sm:flex-initial min-w-[130px] sm:w-[145px]">
                <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
                  <SelectTrigger className="h-10 text-xs rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 w-full shadow-2xs">
                    <div className="flex items-center gap-2 truncate">
                      <ArrowUpDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <SelectValue placeholder="Sort by" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="price_high">Price: High to Low</SelectItem>
                    <SelectItem value="price_low">Price: Low to High</SelectItem>
                    <SelectItem value="patient_name">Patient Name</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="button"
                onClick={() => setRedeemModalOpen(true)}
                className="h-10 px-4 text-xs font-bold rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 shadow-sm shrink-0 w-full sm:w-auto justify-center"
              >
                <ShieldCheck className="w-4 h-4" /> Redeem Patient Voucher
              </Button>
            </div>
          </div>

          {/* Bulk Management Bar if selected */}
          {selectedIds.length > 0 && (
            <div className="flex items-center justify-between bg-primary/10 border border-primary/20 p-2.5 rounded-xl text-xs font-semibold text-primary animate-in fade-in duration-200">
              <div className="flex items-center gap-2">
                <span className="font-bold">{selectedIds.length} estimates selected</span>
                <span className="text-slate-400">|</span>
                <button onClick={() => setSelectedIds([])} className="underline hover:text-primary/80">
                  Deselect all
                </button>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleBulkExportCSV}
                  className="h-8 text-xs font-bold border-primary/30 text-primary hover:bg-primary/10 rounded-lg"
                >
                  <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setDeleteConfirmData({ ids: selectedIds, name: `${selectedIds.length} selected estimates` })}
                  className="h-8 text-xs font-bold rounded-lg"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Selected
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Content Section: List Table View or Grid View */}
        {isLoading ? (
          <div className="bg-white dark:bg-slate-900 p-12 rounded-2xl border border-slate-200 dark:border-slate-800 text-center flex flex-col items-center justify-center">
            <RefreshCw className="w-8 h-8 text-primary animate-spin mb-3" />
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">Loading estimate records...</p>
          </div>
        ) : filteredEstimates.length > 0 ? (
          <div className="flex-1 flex flex-col justify-between space-y-4">
            
            {viewMode === 'list' ? (
              /* Compact List Table View */
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[900px]">
                    <thead>
                      <tr className="bg-slate-50/80 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                        <th className="py-3.5 px-4 w-10 text-center">
                          <Checkbox checked={isAllSelected} onCheckedChange={toggleSelectAll} aria-label="Select all" />
                        </th>
                        <th className="py-3.5 px-4 sm:px-5">Estimate Details & Patient</th>
                        <th className="py-3.5 px-4 sm:px-5">Services / CPT Codes</th>
                        <th className="py-3.5 px-4 sm:px-5 text-right">Pricing Breakdown</th>
                        <th className="py-3.5 px-4 sm:px-5 text-center">Status</th>
                        <th className="py-3.5 px-4 sm:px-5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                      {paginatedEstimates.map((est: any) => {
                        const isSelected = selectedIds.includes(est.id);
                        const estTitle = est.name || est.title || est.procedure || 'General Saved Estimate';
                        const patientName = est.member?.displayName || (est.member?.firstName ? `${est.member.firstName} ${est.member.lastName}`.trim() : null);
                        const items = Array.isArray(est.items) ? est.items : [];
                        const totalCharges = Number(est.totalCharges || 0);
                        const finalAmount = Number(est.finalAmount || totalCharges);
                        const statusStr = String(est.status || '').toUpperCase();

                        let discountPct = Number(est.discount || 0);
                        if (discountPct === 0 && totalCharges > 0 && finalAmount < totalCharges) {
                          discountPct = Math.round(((totalCharges - finalAmount) / totalCharges) * 1000) / 10;
                        }

                        return (
                          <tr
                            key={est.id}
                            className={`hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors ${
                              isSelected ? 'bg-primary/5 dark:bg-primary/10' : ''
                            }`}
                          >
                            <td className="py-4 px-4 text-center align-middle">
                              <Checkbox checked={isSelected} onCheckedChange={() => toggleSelectOne(est.id)} />
                            </td>

                            <td className="py-4 px-4 sm:px-5 align-top min-w-[220px]">
                              <div className="flex items-start gap-3">
                                <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                                  {(patientName || estTitle).charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <h4 
                                    onClick={() => setSelectedEstimate(est)}
                                    className="font-bold text-slate-900 dark:text-white hover:text-primary cursor-pointer truncate text-sm leading-snug"
                                  >
                                    {estTitle}
                                  </h4>
                                  
                                  {patientName ? (
                                    <div className="flex items-center gap-1 text-[11px] font-semibold text-primary mt-0.5 truncate">
                                      <span className="truncate">Patient: {patientName}</span>
                                    </div>
                                  ) : (
                                    <span className="text-[11px] text-slate-400 font-medium mt-0.5 block">Unassigned Template</span>
                                  )}

                                  <div className="flex items-center gap-2.5 text-[10px] text-slate-500 dark:text-slate-400 mt-1.5">
                                    <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3 text-slate-400" /> {new Date(est.createdAt).toLocaleDateString()}</span>
                                    {est.member?.email && (
                                      <span className="flex items-center gap-1 truncate max-w-[140px]"><Mail className="w-3 h-3 text-slate-400" /> {est.member.email}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="py-4 px-4 sm:px-5 align-top">
                              <div className="flex items-center gap-1 flex-wrap max-w-xs">
                                {items.length > 0 ? (
                                  items.slice(0, 3).map((item: any, idx: number) => (
                                    <Badge key={idx} variant="outline" className="font-mono text-[10px] bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 px-1.5 py-0.5">
                                      {item.code || item.cptCode || 'CUSTOM'}
                                    </Badge>
                                  ))
                                ) : (
                                  <Badge variant="outline" className="font-mono text-[10px] bg-slate-50 dark:bg-slate-800 text-slate-500">
                                    {est.cptCodes || 'CUSTOM'}
                                  </Badge>
                                )}
                                {items.length > 3 && (
                                  <span className="text-[10px] font-bold text-slate-400">+{items.length - 3} more</span>
                                )}
                              </div>
                              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">
                                {items.length || 1} item{items.length !== 1 ? 's' : ''} included
                              </p>
                            </td>

                            <td className="py-4 px-4 sm:px-5 align-top text-right whitespace-nowrap">
                              {discountPct > 0 ? (
                                <div className="text-[11px] text-slate-400 line-through">${totalCharges.toFixed(2)}</div>
                              ) : (
                                <div className="text-[11px] text-slate-500">Standard Rate</div>
                              )}
                              <div className="text-base font-extrabold text-slate-900 dark:text-white mt-0.5">
                                ${finalAmount.toFixed(2)}
                              </div>
                              {discountPct > 0 && (
                                <span className="inline-block text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-1.5 py-0.2 rounded-md mt-0.5">
                                  {discountPct.toFixed(1)}% OFF
                                </span>
                              )}
                            </td>

                            <td className="py-4 px-4 sm:px-5 align-top text-center whitespace-nowrap">
                              {getStatusBadge(est)}
                            </td>

                            <td className="py-4 px-4 sm:px-5 align-top text-right whitespace-nowrap">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => setSelectedEstimate(est)}
                                  className="h-8.5 px-3 rounded-xl font-bold text-xs bg-primary hover:bg-primary/90 text-white shadow-2xs flex items-center gap-1.5"
                                >
                                  <SlidersHorizontal className="w-3.5 h-3.5" /> Manage
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOpenDuplicateModal(est)}
                                  className="h-8.5 px-3 rounded-xl font-semibold text-xs border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 flex items-center gap-1.5"
                                  title="Duplicate & Share"
                                >
                                  <Copy className="w-3.5 h-3.5 text-primary" /> Duplicate
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setDeleteConfirmData({ ids: [est.id], name: estTitle })}
                                  className="w-8.5 h-8.5 rounded-xl text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 flex items-center justify-center"
                                  title="Delete Estimate"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : viewMode === 'grid' ? (
              /* Grid View Cards */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginatedEstimates.map((est: any) => {
                  const estTitle = est.name || est.title || est.procedure || 'General Saved Estimate';
                  const patientName = est.member?.displayName || (est.member?.firstName ? `${est.member.firstName} ${est.member.lastName}`.trim() : null);
                  const totalCharges = Number(est.totalCharges || 0);
                  const finalAmount = Number(est.finalAmount || totalCharges);

                  return (
                    <Card key={est.id} className="rounded-2xl border-slate-200 dark:border-slate-800 shadow-xs flex flex-col justify-between">
                      <CardHeader className="p-4 pb-2 flex flex-row items-start justify-between">
                        <div className="min-w-0 pr-2">
                          <CardTitle className="text-base truncate font-extrabold">{estTitle}</CardTitle>
                          <p className="text-xs text-primary font-semibold truncate mt-0.5">
                            {patientName ? `Patient: ${patientName}` : 'Unassigned Template'}
                          </p>
                        </div>
                        {getStatusBadge(est)}
                      </CardHeader>

                      <CardContent className="p-4 pt-2 space-y-3">
                        <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl space-y-1 text-xs border border-slate-100 dark:border-slate-800">
                          <div className="flex justify-between text-slate-500">
                            <span>Total Charges:</span>
                            <span>${totalCharges.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between font-bold text-slate-900 dark:text-white pt-1 border-t border-slate-200 dark:border-slate-800">
                            <span>Final Amount:</span>
                            <span className="text-primary">${finalAmount.toFixed(2)}</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-[11px] text-slate-400">
                          <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {new Date(est.createdAt).toLocaleDateString()}</span>
                          <span>{est.items?.length || 1} items</span>
                        </div>
                      </CardContent>

                      <CardFooter className="p-4 pt-0 flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => setSelectedEstimate(est)}
                          className="w-full rounded-xl text-xs font-bold bg-primary hover:bg-primary/90 text-white shadow-2xs flex items-center justify-center gap-1"
                        >
                          <SlidersHorizontal className="w-3.5 h-3.5" /> Manage
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenDuplicateModal(est)}
                          className="rounded-xl text-xs font-semibold border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 flex items-center gap-1"
                          title="Duplicate & Share"
                        >
                          <Copy className="w-3.5 h-3.5 text-primary" /> Duplicate
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteConfirmData({ ids: [est.id], name: estTitle })}
                          className="w-8 h-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 shrink-0"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </CardFooter>
                    </Card>
                  );
                })}
              </div>
            ) : (
              /* Grouped by Patient View */
              <div className="space-y-6">
                {groupedByPatient.map((group) => (
                  <Card key={group.patientId} className="rounded-2xl border-slate-200 dark:border-slate-800 shadow-xs bg-white dark:bg-slate-900 overflow-hidden">
                    <CardHeader className="p-4 bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0">
                          {group.patientName.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <CardTitle className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-2 flex-wrap">
                            <span className="truncate">{group.patientName}</span>
                            <Badge variant="outline" className="text-[11px] font-bold bg-primary/10 text-primary border-primary/20 px-2 py-0.5 rounded-full shrink-0">
                              {group.estimates.length} {group.estimates.length === 1 ? 'Estimate' : 'Estimates'}
                            </Badge>
                          </CardTitle>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5 flex-wrap">
                            {group.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" /> {group.email}</span>}
                            {group.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" /> {group.phone}</span>}
                            {group.patientId === 'unassigned' && <span>Master practice templates available for quick sharing</span>}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
                        <div className="text-right">
                          <p className="text-[10px] uppercase font-bold text-slate-400">Total Patient Value</p>
                          <p className="text-base font-black text-primary">${group.totalValue.toFixed(2)}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenDuplicateModal(group.estimates[0] || null, group.patientId !== 'unassigned' ? group.patientId : '')}
                          className="h-9 px-3 rounded-xl font-bold text-xs border-primary/30 text-primary hover:bg-primary/10 gap-1.5"
                        >
                          <Copy className="w-3.5 h-3.5" /> Duplicate & Share to Patient
                        </Button>
                      </div>
                    </CardHeader>

                    <CardContent className="p-0 divide-y divide-slate-100 dark:divide-slate-800">
                      {group.estimates.map((est: any) => {
                        const estTitle = est.name || est.title || est.procedure || 'General Saved Estimate';
                        const totalCharges = Number(est.totalCharges || 0);
                        const finalAmount = Number(est.finalAmount || totalCharges);
                        const isSelected = selectedIds.includes(est.id);

                        return (
                          <div key={est.id} className={`p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-slate-50/60 dark:hover:bg-slate-800/30 transition-colors ${isSelected ? 'bg-primary/5' : ''}`}>
                            <div className="flex items-start gap-3 min-w-0">
                              <Checkbox checked={isSelected} onCheckedChange={() => toggleSelectOne(est.id)} className="mt-1" />
                              <div className="min-w-0">
                                <h4 onClick={() => setSelectedEstimate(est)} className="font-bold text-slate-900 dark:text-white hover:text-primary cursor-pointer text-sm truncate">
                                  {estTitle}
                                </h4>
                                <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                                  <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3 text-slate-400" /> {new Date(est.createdAt).toLocaleDateString()}</span>
                                  <span>• {est.items?.length || 1} CPT items</span>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center justify-between sm:justify-end gap-4">
                              <div className="text-right">
                                <div className="text-sm font-extrabold text-slate-900 dark:text-white">${finalAmount.toFixed(2)}</div>
                                <div className="mt-0.5">{getStatusBadge(est.status)}</div>
                              </div>

                              <div className="flex items-center gap-1.5">
                                <Button
                                  size="sm"
                                  onClick={() => setSelectedEstimate(est)}
                                  className="h-8 px-2.5 rounded-xl font-bold text-xs bg-primary text-white shadow-2xs gap-1"
                                >
                                  <SlidersHorizontal className="w-3.5 h-3.5" /> Manage
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOpenDuplicateModal(est)}
                                  className="h-8 px-2.5 rounded-xl font-semibold text-xs border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 gap-1"
                                  title="Duplicate & Share"
                                >
                                  <Copy className="w-3.5 h-3.5 text-primary" /> Duplicate
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setDeleteConfirmData({ ids: [est.id], name: estTitle })}
                                  className="w-8 h-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination Controls */}
            <div className="pt-3.5 sm:pt-4 border-t border-slate-200 dark:border-slate-800">
              <ListPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalCount={filteredEstimates.length}
                limit={limit}
                onLimitChange={setLimit}
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-16 bg-white dark:bg-slate-900 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-base font-bold text-slate-900 dark:text-white">No estimates found</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
              {searchQuery ? "No estimate records matched your current filter query." : "You haven't created any service estimates yet."}
            </p>
            {searchQuery ? (
              <Button variant="link" onClick={() => setSearchQuery('')} className="mt-2 text-xs">Clear Search</Button>
            ) : (
              <Button onClick={() => setLocation('/provider/cost-estimator')} className="mt-4 rounded-xl text-xs font-bold bg-primary text-white">
                <Plus className="w-4 h-4 mr-1" /> Create First Estimate
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteConfirmData}
        onOpenChange={(open) => !open && setDeleteConfirmData(null)}
        title="Delete Estimate Record(s)"
        description={`Are you sure you want to permanently delete "${deleteConfirmData?.name}"? This action cannot be undone.`}
        confirmText="Yes, Delete Estimate"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />

      {/* View Details & Receipt Modal */}
      <Dialog open={!!selectedEstimate} onOpenChange={(open) => !open && setSelectedEstimate(null)}>
        <DialogContent className="max-w-4xl sm:max-w-4xl lg:max-w-5xl w-[94vw] sm:w-[90vw] lg:w-[85vw] rounded-3xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white flex items-center justify-between">
              <span>Estimate Details & Financial Invoice</span>
            </DialogTitle>
          </DialogHeader>
          
          {selectedEstimate && (
            <div className="space-y-5 mt-1">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-50 dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary font-bold text-base shrink-0">
                    {(selectedEstimate.name || selectedEstimate.title || selectedEstimate.member?.displayName || 'E').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-extrabold text-base text-slate-900 dark:text-white break-words">
                      {selectedEstimate.name || selectedEstimate.title || selectedEstimate.procedure || 'General Saved Estimate'}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                      <span>{selectedEstimate.member ? `Patient: ${selectedEstimate.member?.displayName || selectedEstimate.member?.firstName}` : 'Unassigned Template'}</span>
                      {selectedEstimate.member?.email && <span>• {selectedEstimate.member.email}</span>}
                    </div>
                  </div>
                </div>
                {getStatusBadge(selectedEstimate.status)}
              </div>
              
              {/* Contextual Workflow Action Bar */}
              <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-2.5">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <Zap className="w-4 h-4 text-amber-500" /> Available Workflow Actions
                  </h4>
                  <span className="text-[11px] text-slate-400 font-medium">Click an action to perform</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {(String(selectedEstimate.status || '').toUpperCase() === 'SUBMITTED' || String(selectedEstimate.status || '').toUpperCase() === 'PENDING') && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => { const est = selectedEstimate; setSelectedEstimate(null); handleOpenApproveModal(est); }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl gap-1.5 shadow-2xs"
                      >
                        <Check className="w-4 h-4" /> Approve Estimate
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { const est = selectedEstimate; setSelectedEstimate(null); handleOpenCounterModal(est); }}
                        className="border-purple-300 text-purple-800 dark:text-purple-300 hover:bg-purple-50 font-bold text-xs rounded-xl gap-1.5"
                      >
                        <Edit2 className="w-4 h-4" /> Counter & Adjust CPT Codes
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { const est = selectedEstimate; setSelectedEstimate(null); handleOpenRejectModal(est); }}
                        className="border-red-200 text-red-700 dark:text-red-400 hover:bg-red-50 font-bold text-xs rounded-xl gap-1.5"
                      >
                        <X className="w-4 h-4" /> Decline
                      </Button>
                    </>
                  )}

                  {String(selectedEstimate.status || '').toUpperCase() === 'PATIENT_PROPOSED' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => { const est = selectedEstimate; setSelectedEstimate(null); handleAcceptPatientProposal(est); }}
                        className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl gap-1.5 shadow-2xs"
                      >
                        <Check className="w-4 h-4" /> Accept Patient Offer
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { const est = selectedEstimate; setSelectedEstimate(null); handleOpenCounterModal(est); }}
                        className="border-purple-300 text-purple-800 dark:text-purple-300 hover:bg-purple-50 font-bold text-xs rounded-xl gap-1.5"
                      >
                        <Edit2 className="w-4 h-4" /> Counter Offer
                      </Button>
                    </>
                  )}

                  {(String(selectedEstimate.status || '').toUpperCase() === 'APPROVED' || String(selectedEstimate.status || '').toUpperCase() === 'ACCEPTED') && String(selectedEstimate.status || '').toUpperCase() !== 'PAID' && String(selectedEstimate.status || '').toUpperCase() !== 'COMPLETED' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => { const est = selectedEstimate; setSelectedEstimate(null); handleOpenPayModal(est); }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl gap-1.5 shadow-md shadow-emerald-600/20"
                      >
                        <Receipt className="w-4 h-4" /> Mark as Paid Invoice
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => { const est = selectedEstimate; setSelectedEstimate(null); handleOpenCompleteModal(est); }}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl gap-1.5 shadow-md shadow-blue-600/20"
                      >
                        <ShieldCheck className="w-4 h-4" /> Mark Service Rendered / Completed
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { const est = selectedEstimate; setSelectedEstimate(null); handleOpenCounterModal(est); }}
                        className="border-purple-300 text-purple-800 dark:text-purple-300 hover:bg-purple-50 font-bold text-xs rounded-xl gap-1.5"
                      >
                        <Edit2 className="w-4 h-4" /> Modify Pricing
                      </Button>
                    </>
                  )}

                  {/* Secondary Quick Action Tools */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { const est = selectedEstimate; setSelectedEstimate(null); handleOpenDuplicateModal(est); }}
                    className="border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 font-semibold text-xs rounded-xl gap-1.5"
                  >
                    <Copy className="w-3.5 h-3.5 text-primary" /> Duplicate & Share
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { const est = selectedEstimate; setSelectedEstimate(null); handleOpenAssignModal(est); }}
                    className="border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 font-semibold text-xs rounded-xl gap-1.5"
                  >
                    <Share2 className="w-3.5 h-3.5 text-blue-600" /> Send to Patient
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { const est = selectedEstimate; setSelectedEstimate(null); handleOpenConvertModal(est); }}
                    className="border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 font-semibold text-xs rounded-xl gap-1.5"
                  >
                    <Package className="w-3.5 h-3.5 text-purple-600" /> Save as Bundle
                  </Button>
                </div>
              </div>

              {/* Service Rendered Date Highlight Badge */}
              {selectedEstimate.serviceRenderedDate && (
                <div className="p-3 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-2xl flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 font-bold text-blue-900 dark:text-blue-200">
                    <ShieldCheck className="w-4 h-4 text-blue-600" /> Date Service Rendered:
                    <span className="font-extrabold text-sm">{new Date(selectedEstimate.serviceRenderedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                  </div>
                  <Badge variant="outline" className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border-blue-300 font-bold">
                    Procedure Verified
                  </Badge>
                </div>
              )}

              {/* Payment Receipt Banner if Paid */}
              {(selectedEstimate.paymentDetails || String(selectedEstimate.status).toUpperCase() === 'PAID') && (
                <div className="p-4 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-2xl space-y-2 text-xs text-emerald-900 dark:text-emerald-200">
                  <div className="flex items-center justify-between font-black text-sm text-emerald-950 dark:text-emerald-100 border-b border-emerald-200/80 pb-2">
                    <span className="flex items-center gap-1.5"><Receipt className="w-4 h-4 text-emerald-600" /> Payment Received</span>
                    <span>${Number(selectedEstimate.paymentDetails?.amountPaid || selectedEstimate.finalAmount || 0).toFixed(2)}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1 font-medium">
                    <div>
                      <span className="text-slate-500 block">Payment Method:</span>
                      <strong className="text-emerald-950 dark:text-emerald-200">{selectedEstimate.paymentDetails?.paymentMethod || 'Credit/Debit Card'}</strong>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Payment Date:</span>
                      <strong>{new Date(selectedEstimate.paymentDetails?.paymentDate || selectedEstimate.updatedAt).toLocaleDateString()}</strong>
                    </div>
                  </div>

                  {selectedEstimate.paymentDetails?.paymentReference && (
                    <div className="pt-1">
                      <span className="text-slate-500">Transaction / Ref #: </span>
                      <span className="font-mono font-bold text-emerald-900 dark:text-emerald-200">{selectedEstimate.paymentDetails.paymentReference}</span>
                    </div>
                  )}

                  {selectedEstimate.paymentDetails?.paymentNote && (
                    <div className="p-2 bg-white/70 dark:bg-black/20 rounded-lg italic border border-emerald-200/50 mt-1">
                      "{selectedEstimate.paymentDetails.paymentNote}"
                    </div>
                  )}
                </div>
              )}

              {/* Included CPT Codes & GFE Financial Breakdown Table */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center justify-between">
                  <span>Included Medical Services & GFE Itemized Breakdown</span>
                  <span className="text-[11px] text-slate-400 font-normal">8-Column GFE Structure</span>
                </h4>
                <div className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-2xs">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left border-collapse min-w-[760px]">
                      <thead className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold border-b border-slate-200 dark:border-slate-800 text-[11px] uppercase tracking-wider">
                        <tr>
                          <th className="p-3 w-24">Billing Code</th>
                          <th className="p-3">Service Description</th>
                          <th className="p-3 w-28">Code Category</th>
                          <th className="p-3 text-right w-28">FS Base Rate</th>
                          <th className="p-3 text-right w-28">FS Discount</th>
                          <th className="p-3 text-right w-28">Provider Rate</th>
                          <th className="p-3 text-right w-28">GFE Discount</th>
                          <th className="p-3 text-right w-32">Patient Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-medium">
                        {(selectedEstimate.items || []).map((item: any, idx: number) => {
                          const qty = Number(item.quantity || 1);
                          const stdPrice = Number(item.standardPrice || item.unitPrice || 0);
                          const provRate = Number(item.unitPrice || 0);
                          const fsAdj = Math.max(0, stdPrice - provRate);
                          const gfeAdj = Number(item.gfeAdjustment || 0);
                          const patCharge = item.patientCharges !== undefined 
                            ? Number(item.patientCharges) 
                            : Math.max(0, provRate - gfeAdj) * qty;

                          const rawCat = typeof item.category === 'object' ? item.category?.name : (item.codeCategory || item.category);
                          const isInvalidCat = rawCat && ["PFS", "CLFS", "CPT", "HCPCS"].includes(String(rawCat).trim().toUpperCase());
                          let resolvedCat = (!isInvalidCat && rawCat) ? String(rawCat) : "";
                          if (!resolvedCat && masterCptList?.length) {
                            const m = masterCptList.find((mc: any) => mc.code === (item.code || item.cptCode));
                            if (m) {
                              const mCat = typeof m.category === 'object' ? m.category?.name : m.category;
                              if (mCat && !["PFS", "CLFS", "CPT", "HCPCS"].includes(String(mCat).trim().toUpperCase())) {
                                resolvedCat = String(mCat);
                              }
                            }
                          }
                          if (!resolvedCat) resolvedCat = "General";

                          return (
                            <tr key={idx} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/30">
                              <td className="p-3 font-mono font-bold text-primary whitespace-nowrap">{item.code || item.cptCode || 'CUSTOM'}</td>
                              <td className="p-3 text-slate-900 dark:text-white font-semibold max-w-xs break-words">{item.description || item.name || 'Medical Service'}</td>
                              <td className="p-3 text-slate-500 whitespace-nowrap">{resolvedCat}</td>
                              <td className="p-3 text-right text-slate-500 whitespace-nowrap">${stdPrice.toFixed(2)}</td>
                              <td className="p-3 text-right text-emerald-600 dark:text-emerald-400 whitespace-nowrap">-{fsAdj > 0 ? `$${fsAdj.toFixed(2)}` : '$0.00'}</td>
                              <td className="p-3 text-right font-bold text-slate-900 dark:text-white whitespace-nowrap">${provRate.toFixed(2)}</td>
                              <td className="p-3 text-right text-purple-600 dark:text-purple-400 whitespace-nowrap">-{gfeAdj > 0 ? `$${gfeAdj.toFixed(2)}` : '$0.00'}</td>
                              <td className="p-3 text-right font-black text-emerald-700 dark:text-emerald-300 text-sm whitespace-nowrap">${patCharge.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Audit & Comment History Log */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between">
                  <span className="flex items-center gap-1.5"><History className="w-4 h-4 text-primary" /> Audit & Back-and-Forth Comment History ({selectedEstimate.negotiationHistory?.length || 0})</span>
                  <span className="text-[10px] text-slate-400 font-normal">Immutable Audit Log</span>
                </h4>

                {/* Direct Audit Comment Form */}
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Add audit comment or patient note to trail..."
                    value={newAuditComment}
                    onChange={e => setNewAuditComment(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddAuditCommentSubmit()}
                    className="text-xs rounded-xl bg-slate-50 dark:bg-slate-900 border-slate-200"
                  />
                  <Button
                    size="sm"
                    onClick={handleAddAuditCommentSubmit}
                    disabled={!newAuditComment.trim() || updateStatus.isPending}
                    className="rounded-xl text-xs font-bold bg-primary text-white shrink-0"
                  >
                    Post Note
                  </Button>
                </div>

                {Array.isArray(selectedEstimate.negotiationHistory) && selectedEstimate.negotiationHistory.length > 0 ? (
                  <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
                    {selectedEstimate.negotiationHistory.map((hist: any, index: number) => (
                      <div key={hist.id || index} className="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 space-y-1 text-xs">
                        <div className="flex items-center justify-between font-semibold">
                          <div className="flex items-center gap-1.5">
                            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                              hist.by === 'PROVIDER' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300' : 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300'
                            }`}>
                              {hist.by === 'PROVIDER' ? 'Provider Note' : 'Patient Comment'}
                            </span>
                            <span className="text-slate-900 dark:text-white font-bold">{hist.authorName}</span>
                          </div>
                          <span className="text-[10px] text-slate-400">{new Date(hist.timestamp).toLocaleString()}</span>
                        </div>
                        
                        <div className="flex items-center justify-between text-slate-600 dark:text-slate-400 pt-1">
                          <span>Status: <strong className="text-slate-900 dark:text-white">{hist.status}</strong></span>
                          <span className="font-bold text-primary">${Number(hist.finalAmount || 0).toFixed(2)} ({hist.discount || 0}% OFF)</span>
                        </div>

                        {hist.serviceRenderedDate && (
                          <div className="text-[11px] font-bold text-blue-600 dark:text-blue-400 pt-0.5 flex items-center gap-1">
                            <ShieldCheck className="w-3.5 h-3.5" /> Date Service Rendered: {new Date(hist.serviceRenderedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </div>
                        )}

                        {hist.note && (
                          <div className="p-2 bg-white dark:bg-slate-950 rounded-lg italic text-slate-700 dark:text-slate-300 mt-1 border border-slate-100 dark:border-slate-800">
                            "{hist.note}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No previous audit comments logged yet.</p>
                )}
              </div>
              
              {/* Financial GFE Summary Breakdown Box */}
              <div className="bg-slate-50 dark:bg-slate-900 p-4.5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-2 text-xs sm:text-sm">
                <div className="flex justify-between text-slate-500">
                  <span>Fee Schedule Base Rate Total:</span>
                  <span className="font-semibold">${(selectedEstimate.items || []).reduce((s: number, i: any) => s + (Number(i.standardPrice || i.unitPrice || 0) * Number(i.quantity || 1)), 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-emerald-600 font-semibold">
                  <span>Fee Schedule Discount Total:</span>
                  <span>-${(selectedEstimate.items || []).reduce((s: number, i: any) => s + Math.max(0, (Number(i.standardPrice || i.unitPrice || 0) - Number(i.unitPrice || 0)) * Number(i.quantity || 1)), 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-700 dark:text-slate-300 font-bold border-t border-slate-200/60 dark:border-slate-800 pt-1">
                  <span>Provider Rate Total:</span>
                  <span>${Number(selectedEstimate.totalCharges || selectedEstimate.finalAmount || 0).toFixed(2)}</span>
                </div>
                {Number(selectedEstimate.discount || 0) > 0 && (
                  <div className="flex justify-between text-purple-600 font-semibold">
                    <span>Good Faith Estimate Discount ({Number(selectedEstimate.discount).toFixed(1)}%):</span>
                    <span>-${(Number(selectedEstimate.totalCharges || selectedEstimate.finalAmount || 0) - Number(selectedEstimate.finalAmount || 0)).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-black text-base sm:text-lg pt-2 border-t border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
                  <span>Final Patient Responsibility (Amount Due):</span>
                  <span className="text-emerald-600 dark:text-emerald-400">${Number(selectedEstimate.finalAmount || 0).toFixed(2)}</span>
                </div>
              </div>

              {/* No Surprises Act GFE Legal Disclaimer */}
              <div className="p-3 bg-slate-100 dark:bg-slate-800/60 rounded-xl text-[11px] text-slate-500 leading-relaxed border border-slate-200 dark:border-slate-800">
                <strong className="text-slate-700 dark:text-slate-300">Notice & Price Transparency Disclaimer:</strong> This Good Faith Estimate shows the costs of items and services that are reasonably expected for your health care needs under the No Surprises Act. This estimate is based on information known at the time of issue and does not include unknown or unexpected emergency costs that may arise during medical treatment.
              </div>

              {/* Dual Export Buttons (PDF & CSV) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <Button
                  onClick={() => {
                    exportEstimateToPDF({
                      title: selectedEstimate.name || selectedEstimate.title || 'Good Faith Cost Estimate',
                      patientName: selectedEstimate.member?.displayName || (selectedEstimate.member?.firstName ? `${selectedEstimate.member.firstName} ${selectedEstimate.member.lastName}` : 'Patient'),
                      patientEmail: selectedEstimate.member?.email,
                      date: selectedEstimate.createdAt,
                      serviceRenderedDate: selectedEstimate.serviceRenderedDate,
                      items: (selectedEstimate.items || []).map((i: any) => {
                        const rawCat = typeof i.category === 'object' ? i.category?.name : (i.codeCategory || i.category);
                        const isInvalid = rawCat && ["PFS", "CLFS", "CPT", "HCPCS"].includes(String(rawCat).trim().toUpperCase());
                        let cat = (!isInvalid && rawCat) ? String(rawCat) : "";
                        if (!cat && masterCptList?.length) {
                          const m = masterCptList.find((mc: any) => mc.code === (i.code || i.cptCode));
                          if (m) {
                            const mCat = typeof m.category === 'object' ? m.category?.name : m.category;
                            if (mCat && !["PFS", "CLFS", "CPT", "HCPCS"].includes(String(mCat).trim().toUpperCase())) {
                              cat = String(mCat);
                            }
                          }
                        }
                        return {
                          code: i.code || 'CUSTOM',
                          description: i.description || 'Medical Service',
                          category: cat || 'General Procedure',
                          quantity: Number(i.quantity || 1),
                          standardPrice: Number(i.standardPrice || i.unitPrice || 0),
                          unitPrice: Number(i.unitPrice || 0),
                          gfeAdjustment: Number(i.gfeAdjustment || 0)
                        };
                      }),
                      totalCharges: Number(selectedEstimate.totalCharges || 0),
                      discount: Number(selectedEstimate.discount || 0),
                      finalAmount: Number(selectedEstimate.finalAmount || 0)
                    });
                  }}
                  className="w-full gap-2 font-bold text-xs h-10 rounded-xl bg-primary text-white shadow-md shadow-primary/20"
                >
                  <FileText className="w-4 h-4" /> Download / Print Official PDF Invoice
                </Button>

                <Button
                  variant="outline"
                  onClick={() => {
                    exportEstimateToCSV({
                      title: selectedEstimate.name || selectedEstimate.title || 'cost-estimate',
                      patientName: selectedEstimate.member?.displayName || 'Patient',
                      patientEmail: selectedEstimate.member?.email,
                      date: selectedEstimate.createdAt,
                      serviceRenderedDate: selectedEstimate.serviceRenderedDate,
                      items: (selectedEstimate.items || []).map((i: any) => {
                        const rawCat = typeof i.category === 'object' ? i.category?.name : (i.codeCategory || i.category);
                        const isInvalid = rawCat && ["PFS", "CLFS", "CPT", "HCPCS"].includes(String(rawCat).trim().toUpperCase());
                        let cat = (!isInvalid && rawCat) ? String(rawCat) : "";
                        if (!cat && masterCptList?.length) {
                          const m = masterCptList.find((mc: any) => mc.code === (i.code || i.cptCode));
                          if (m) {
                            const mCat = typeof m.category === 'object' ? m.category?.name : m.category;
                            if (mCat && !["PFS", "CLFS", "CPT", "HCPCS"].includes(String(mCat).trim().toUpperCase())) {
                              cat = String(mCat);
                            }
                          }
                        }
                        return {
                          code: i.code || 'CUSTOM',
                          description: i.description || 'Medical Service',
                          category: cat || 'General',
                          quantity: Number(i.quantity || 1),
                          standardPrice: Number(i.standardPrice || i.unitPrice || 0),
                          unitPrice: Number(i.unitPrice || 0)
                        };
                      }),
                      totalCharges: Number(selectedEstimate.totalCharges || 0),
                      discount: Number(selectedEstimate.discount || 0),
                      finalAmount: Number(selectedEstimate.finalAmount || 0)
                    });
                  }}
                  className="w-full gap-2 font-bold text-xs h-10 rounded-xl border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900"
                >
                  <Download className="w-4 h-4 text-emerald-600" /> Export CSV Financial Record
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark as Paid / Record Payment Dialog */}
      <Dialog open={payModalOpen} onOpenChange={setPayModalOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-600" /> Record Payment & Mark Paid
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3.5 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl text-xs space-y-1 border border-emerald-200">
              <div className="flex justify-between font-bold text-emerald-900 dark:text-emerald-300">
                <span>Invoice / Estimate:</span>
                <span>{payEstimate?.name || payEstimate?.title || 'Custom Estimate'}</span>
              </div>
              <div className="flex justify-between font-black text-emerald-950 dark:text-emerald-100 text-sm pt-1 border-t border-emerald-200/60">
                <span>Final Invoice Amount:</span>
                <span>${Number(payEstimate?.finalAmount || 0).toFixed(2)}</span>
              </div>
            </div>

            <div>
              <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">Amount Collected ($) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={payForm.amountPaid}
                onChange={e => setPayForm({ ...payForm, amountPaid: e.target.value })}
                className="mt-1 rounded-xl text-xs font-bold text-emerald-600"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">Payment Method *</Label>
                <Select value={payForm.paymentMethod} onValueChange={v => setPayForm({ ...payForm, paymentMethod: v })}>
                  <SelectTrigger className="mt-1 rounded-xl text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Credit/Debit Card">Credit / Debit Card</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="ACH / Bank Transfer">ACH / Bank Transfer</SelectItem>
                    <SelectItem value="Check">Check</SelectItem>
                    <SelectItem value="Insurance Benefit">Insurance Benefit</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">Payment Date *</Label>
                <Input
                  type="date"
                  value={payForm.paymentDate}
                  onChange={e => setPayForm({ ...payForm, paymentDate: e.target.value })}
                  className="mt-1 rounded-xl text-xs"
                />
              </div>
            </div>

            <div>
              <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">Date Service Rendered *</Label>
              <Input
                type="date"
                value={payForm.serviceRenderedDate}
                onChange={e => setPayForm({ ...payForm, serviceRenderedDate: e.target.value })}
                className="mt-1 rounded-xl text-xs font-bold text-blue-600"
              />
            </div>

            <div>
              <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">Reference / Check / Auth # (Optional)</Label>
              <Input
                type="text"
                placeholder="e.g. TXN-98421 or Check #4021"
                value={payForm.paymentReference}
                onChange={e => setPayForm({ ...payForm, paymentReference: e.target.value })}
                className="mt-1 rounded-xl text-xs font-mono"
              />
            </div>

            <div>
              <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">Payment Note / Receipt Message (Optional)</Label>
              <Input
                type="text"
                placeholder="e.g. Received copay via POS terminal..."
                value={payForm.paymentNote}
                onChange={e => setPayForm({ ...payForm, paymentNote: e.target.value })}
                className="mt-1 rounded-xl text-xs"
              />
            </div>

            <Button
              onClick={handleMarkAsPaidSubmit}
              disabled={!payForm.amountPaid || updateStatus.isPending}
              className="w-full rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white h-11 shadow-md shadow-emerald-600/20"
            >
              {updateStatus.isPending ? "Recording Payment..." : "Confirm & Mark Paid"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Counter Offer & CPT Editing Modal */}
      <Dialog open={counterModalOpen} onOpenChange={setCounterModalOpen}>
        <DialogContent className="max-w-xl w-full rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-600" /> Counter Offer & CPT Revision
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200 mb-1.5 block">
                Associated CPT Codes & Services (Add/Delete CPTs)
              </Label>
              <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden text-xs">
                <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-48 overflow-y-auto">
                  {counterItems.map((item, idx) => (
                    <div key={item.id || idx} className="p-2.5 flex items-center justify-between gap-2 bg-slate-50 dark:bg-slate-900">
                      <div className="min-w-0 flex-1 flex items-center gap-2">
                        <Badge variant="outline" className="font-mono font-bold text-primary shrink-0">
                          {item.code}
                        </Badge>
                        <span className="truncate text-slate-700 dark:text-slate-300 font-medium">{item.description}</span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-bold text-slate-900 dark:text-white">${(item.unitPrice * item.quantity).toFixed(2)}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveCounterCpt(idx)}
                          className="w-6 h-6 text-slate-400 hover:text-red-600 rounded-md"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="p-2.5 bg-slate-100 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <Select
                    onValueChange={(val) => {
                      const matched = masterCptList.find((mc: any) => String(mc.code || mc.cptCode) === String(val));
                      if (matched) {
                        setNewCptCode(String(matched.code || matched.cptCode));
                        setNewCptDesc(String(matched.description || matched.shortDescription || matched.longDescription || "Medical Service"));
                        if (matched.averageCost || matched.basePrice) {
                          setNewCptPrice(String(matched.averageCost || matched.basePrice));
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs font-mono bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-lg min-w-[140px]">
                      <SelectValue placeholder="Lookup Master CPT..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-56">
                      {masterCptList.map((mc: any, i: number) => (
                        <SelectItem key={i} value={String(mc.code || mc.cptCode)}>
                          <span className="font-mono font-bold mr-1.5 text-indigo-600 dark:text-indigo-400">{mc.code || mc.cptCode}</span>
                          <span className="text-slate-500 truncate max-w-[200px]">{mc.description || mc.shortDescription}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Input
                    placeholder="Billing Code"
                    value={newCptCode}
                    readOnly
                    disabled
                    className="w-24 h-8 text-xs font-mono font-bold uppercase bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 cursor-not-allowed select-none"
                  />
                  <Input
                    placeholder="Master Description..."
                    value={newCptDesc}
                    readOnly
                    disabled
                    className="flex-1 h-8 text-xs bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 cursor-not-allowed select-none"
                  />
                  <Input
                    placeholder="Price ($)"
                    type="number"
                    value={newCptPrice}
                    onChange={e => setNewCptPrice(e.target.value)}
                    className="w-20 h-8 text-xs font-bold bg-white dark:bg-slate-900"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddCounterCpt}
                    disabled={!newCptCode}
                    className="h-8 px-2.5 text-xs font-bold bg-primary text-white rounded-lg"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-1">
              <div>
                <Label className="font-semibold text-xs">Discount (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={counterForm.discountPercent}
                  onChange={e => {
                    const discStr = e.target.value;
                    const disc = parseFloat(discStr) || 0;
                    const total = counterItems.reduce((acc, i) => acc + (i.unitPrice * i.quantity), 0);
                    const calcPrice = total > 0 ? Math.max(0, total - (total * disc) / 100).toFixed(2) : counterForm.finalPrice;
                    setCounterForm({ ...counterForm, discountPercent: discStr, finalPrice: calcPrice });
                  }}
                  className="mt-1 rounded-xl font-bold text-xs"
                />
              </div>
              <div>
                <Label className="font-semibold text-xs">Resubmitted Final Price ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={counterForm.finalPrice}
                  onChange={e => {
                    const priceStr = e.target.value;
                    const price = parseFloat(priceStr) || 0;
                    const total = counterItems.reduce((acc, i) => acc + (i.unitPrice * i.quantity), 0);
                    const calcDisc = total > 0 ? Math.max(0, ((total - price) / total) * 100).toFixed(1) : "0";
                    setCounterForm({ ...counterForm, finalPrice: priceStr, discountPercent: calcDisc });
                  }}
                  className="mt-1 rounded-xl font-bold text-xs text-purple-600"
                />
              </div>
            </div>

            <div>
              <Label className="font-semibold text-xs">Counter Note / Explanation for Patient</Label>
              <Input
                type="text"
                placeholder="e.g. Adjusted CPT codes and offered discounted package rate..."
                value={counterForm.note}
                onChange={e => setCounterForm({ ...counterForm, note: e.target.value })}
                className="mt-1 rounded-xl text-xs"
              />
            </div>

            <Button onClick={handleSendCounterOfferSubmit} disabled={!counterForm.finalPrice || updateStatus.isPending} className="w-full rounded-xl font-bold bg-purple-600 hover:bg-purple-700 text-white h-11">
              {updateStatus.isPending ? "Resubmitting..." : "Resubmit Counter Offer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert to Bundle Dialog */}
      <Dialog open={convertModalOpen} onOpenChange={setConvertModalOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Convert Estimate to Bundle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="font-semibold text-xs">Bundle Package Name</Label>
              <Input
                type="text"
                placeholder="e.g. Total Hip Replacement Bundle"
                value={bundleForm.name}
                onChange={e => setBundleForm({ ...bundleForm, name: e.target.value })}
                className="mt-1 rounded-xl text-xs"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-semibold text-xs">Discount (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={bundleForm.discountPercent}
                  onChange={e => setBundleForm({ ...bundleForm, discountPercent: e.target.value })}
                  className="mt-1 rounded-xl text-xs"
                />
              </div>
              <div>
                <Label className="font-semibold text-xs">Final Package Price ($)</Label>
                <Input
                  type="number"
                  min="0"
                  value={bundleForm.finalPrice}
                  onChange={e => setBundleForm({ ...bundleForm, finalPrice: e.target.value })}
                  className="mt-1 rounded-xl text-xs font-bold text-purple-600"
                />
              </div>
            </div>

            <Button onClick={handleSaveConvertedBundle} disabled={!bundleForm.name || createBundle.isPending} className="w-full rounded-xl font-bold bg-purple-600 hover:bg-purple-700 text-white h-11">
              {createBundle.isPending ? "Converting..." : "Save as Practice Bundle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Verify & Redeem Patient Voucher Modal */}
      <Dialog open={redeemModalOpen} onOpenChange={setRedeemModalOpen}>
        <DialogContent className="max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-primary dark:text-blue-400 font-extrabold text-lg">
              <ShieldCheck className="w-6 h-6 text-primary" /> Redeem Patient Service Voucher
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <p className="text-xs text-slate-600 dark:text-slate-400">
              Enter the patient's Redemption Voucher Code (presented at check-in) to verify and mark their Good Faith Estimate as service rendered & REDEEMED.
            </p>

            <div>
              <Label className="text-xs font-bold text-slate-700 dark:text-slate-200">
                Redemption Voucher Code
              </Label>
              <div className="relative mt-1.5">
                <Input
                  type="text"
                  placeholder="e.g. RED-89A7-9281"
                  value={voucherCodeInput}
                  onChange={e => setVoucherCodeInput(e.target.value.toUpperCase())}
                  className="rounded-xl text-base font-mono font-bold tracking-wider text-primary dark:text-cyan-400 uppercase h-11 pl-3 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setRedeemModalOpen(false)}
                className="rounded-xl text-xs font-semibold"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleRedeemVoucher()}
                disabled={!voucherCodeInput || redeemVoucherMutation.isPending}
                className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground shadow-md text-xs gap-1.5 px-4 h-10"
              >
                <ShieldCheck className="w-4 h-4" />
                {redeemVoucherMutation.isPending ? "Verifying..." : "Verify & Redeem Code"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Estimate to Patient Dialog */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Send Estimate to Patient</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">Select Patient *</Label>
              <Select value={selectedPatientId} onValueChange={setSelectedPatientId}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Choose a patient..." /></SelectTrigger>
                <SelectContent>
                  {patients.map((p: any) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim()} ({p.email || p.phone || 'Patient'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAssignPatientSubmit} disabled={!selectedPatientId || assignPatient.isPending} className="w-full rounded-xl font-bold bg-primary hover:bg-primary/90 text-white h-11">
              {assignPatient.isPending ? "Sending..." : "Send Estimate to Patient"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Provider Approve Estimate Dialog */}
      <Dialog open={approveModalOpen} onOpenChange={setApproveModalOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-600" /> Approve Estimate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl text-xs space-y-1 border border-emerald-200">
              <div className="flex justify-between font-extrabold text-emerald-900 dark:text-emerald-200 text-sm">
                <span>Approved Amount:</span>
                <span>${Number(approveEstimate?.finalAmount || approveEstimate?.totalCharges || 0).toFixed(2)}</span>
              </div>
            </div>

            <div>
              <Label className="font-semibold text-xs">Note for Member (Optional)</Label>
              <Input
                type="text"
                placeholder="e.g. Approved and ready for booking."
                value={approveNote}
                onChange={e => setApproveNote(e.target.value)}
                className="mt-1 rounded-xl text-xs"
              />
            </div>

            <Button onClick={handleSendApproveSubmit} disabled={updateStatus.isPending} className="w-full rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white h-11">
              {updateStatus.isPending ? "Approving..." : "Confirm & Approve"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Provider Reject Estimate Dialog */}
      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-red-600">Decline Estimate Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="font-semibold text-xs">Reason / Feedback Note</Label>
              <Input
                type="text"
                placeholder="e.g. In-person evaluation required prior to estimate."
                value={rejectNote}
                onChange={e => setRejectNote(e.target.value)}
                className="mt-1 rounded-xl text-xs"
              />
            </div>

            <Button onClick={handleSendRejectSubmit} disabled={updateStatus.isPending} variant="destructive" className="w-full rounded-xl font-bold h-11">
              {updateStatus.isPending ? "Declining..." : "Confirm & Decline"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark Service Rendered & Completed Dialog */}
      <Dialog open={completeModalOpen} onOpenChange={setCompleteModalOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" /> Mark Service Rendered & Completed
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3.5 bg-blue-50 dark:bg-blue-950/40 rounded-xl text-xs space-y-1 border border-blue-200">
              <div className="flex justify-between font-bold text-blue-900 dark:text-blue-300">
                <span>Estimate Package:</span>
                <span>{completeEstimate?.name || completeEstimate?.title || 'Medical Procedure'}</span>
              </div>
              <div className="flex justify-between font-extrabold text-blue-950 dark:text-blue-100 text-sm pt-1 border-t border-blue-200/60">
                <span>Agreed Responsibility:</span>
                <span>${Number(completeEstimate?.finalAmount || 0).toFixed(2)}</span>
              </div>
            </div>

            <div>
              <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">Date Service Rendered * (Day Procedure Was Performed)</Label>
              <Input
                type="date"
                value={completeForm.serviceRenderedDate}
                onChange={e => setCompleteForm({ ...completeForm, serviceRenderedDate: e.target.value })}
                className="mt-1 rounded-xl text-xs font-bold text-blue-600"
              />
            </div>

            <div>
              <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">Clinical / Completion Note (Optional)</Label>
              <Input
                type="text"
                placeholder="e.g. Procedure successfully performed at main clinic facility..."
                value={completeForm.note}
                onChange={e => setCompleteForm({ ...completeForm, note: e.target.value })}
                className="mt-1 rounded-xl text-xs"
              />
            </div>

            <Button
              onClick={handleMarkAsCompletedSubmit}
              disabled={!completeForm.serviceRenderedDate || updateStatus.isPending}
              className="w-full rounded-xl font-bold bg-blue-600 hover:bg-blue-700 text-white h-11 shadow-md shadow-blue-600/20"
            >
              {updateStatus.isPending ? "Recording Completion..." : "Confirm Service Rendered & Complete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate & Share to New Patient Dialog */}
      <Dialog open={duplicateModalOpen} onOpenChange={setDuplicateModalOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-white">
              <Copy className="w-5 h-5 text-primary" /> Duplicate & Share Estimate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl text-xs space-y-1 border border-slate-200 dark:border-slate-800">
              <div className="flex justify-between font-bold text-slate-900 dark:text-white">
                <span>Source Estimate:</span>
                <span className="truncate max-w-[200px]">{duplicateEstimate?.name || duplicateEstimate?.title || 'Cost Estimate'}</span>
              </div>
              <div className="flex justify-between text-slate-500 pt-1 border-t border-slate-200 dark:border-slate-800">
                <span>Original Amount:</span>
                <span className="font-semibold text-primary">${Number(duplicateEstimate?.finalAmount || duplicateEstimate?.totalCharges || 0).toFixed(2)}</span>
              </div>
            </div>

            <div>
              <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">Duplicated Estimate Title *</Label>
              <Input
                type="text"
                value={duplicateForm.title}
                onChange={e => setDuplicateForm({ ...duplicateForm, title: e.target.value })}
                className="mt-1 rounded-xl text-xs"
              />
            </div>

            <div>
              <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">Target Patient (Optional - Leave blank for Master Template)</Label>
              <Select value={duplicateForm.patientId || "unassigned"} onValueChange={v => setDuplicateForm({ ...duplicateForm, patientId: v === "unassigned" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Select patient or save as template..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Save as Practice Master Template (Unassigned)</SelectItem>
                  {patients.map((p: any) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.displayName || p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim()} ({p.email || p.phone || 'Patient'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {duplicateForm.patientId && (
              <div>
                <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">Initial Status</Label>
                <Select value={duplicateForm.status} onValueChange={v => setDuplicateForm({ ...duplicateForm, status: v })}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PENDING">Pending Review (Sent to Patient)</SelectItem>
                    <SelectItem value="SAVED">Draft / Saved Quote</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">Note / Instructions for Patient</Label>
              <Input
                type="text"
                placeholder="e.g. Good Faith Estimate cloned for upcoming consultation..."
                value={duplicateForm.note}
                onChange={e => setDuplicateForm({ ...duplicateForm, note: e.target.value })}
                className="mt-1 rounded-xl text-xs"
              />
            </div>

            <Button
              onClick={handleDuplicateSubmit}
              disabled={!duplicateForm.title || createCostEstimate.isPending}
              className="w-full rounded-xl font-bold bg-primary hover:bg-primary/90 text-white h-11 shadow-md shadow-primary/20"
            >
              {createCostEstimate.isPending ? "Duplicating..." : duplicateForm.patientId ? "Duplicate & Share with Patient" : "Duplicate as Practice Template"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </ProviderLayout>
  );
}
